"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@/components/ui/modal";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { ApiError } from "@/lib/api/error";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { previewAtsLinkStageMap } from "@/lib/api/generated";
import { useAtsConnections } from "../hooks/use-ats-connections";
import { useAtsRemoteJobs } from "../hooks/use-ats-remote-jobs";
import { useConnectionSyncSettings } from "../hooks/use-sync-settings";
import { useLinkJob, useRelinkPreview } from "../hooks/use-ats-job-link";
import {
  formatAtsJobLabel,
  isRemoteJobSelectable,
  remoteJobClosedBadge,
} from "../lib/ats-job-picker";
import {
  StageMapStep,
  suggestStageMap,
  type RemoteStageChoice,
  type ShapeDiff,
} from "./stage-map-step";

type LinkPath = "resume" | "different_remote" | "first_link";

function parseShapeDiff(raw: unknown): ShapeDiff | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  return {
    local_only: Array.isArray(d.local_only)
      ? d.local_only.filter((x): x is string => typeof x === "string")
      : [],
    remote_only: Array.isArray(d.remote_only)
      ? d.remote_only.filter((x): x is string => typeof x === "string")
      : [],
    order_changed: d.order_changed === true,
  };
}

function requiresShapeAck(
  shapeMismatch: boolean,
  path: string,
): boolean {
  return (
    shapeMismatch && (path === "resume" || path === "different_remote")
  );
}

export interface JobLinkDialogStage {
  id: string;
  name: string;
}

interface Props {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked?: () => void;
  /**
   * Present when the job has a dormant (previously-linked) ATS identity —
   * enables the Resume panel. `remoteJobId` is required for a resume attempt;
   * omit/nullify when unknown and the dialog falls back to the normal picker.
   */
  dormant?: {
    connectorName: string;
    connectionId: string;
    remoteJobId: string;
  } | null;
  /**
   * The job's current pipeline stages. The dialog has no stage query of its
   * own, so the caller (the Job Settings panel) passes these through — used
   * purely for cosmetic labels in the reactive
   * stage-map step (occupied_stage_ids -> names). If a stage id isn't found
   * here the id itself is shown as a fallback label.
   */
  jobStages?: JobLinkDialogStage[];
}

interface StageMapPending {
  connectionId: string;
  remoteJobId: string;
  occupiedStageIds: string[];
  remoteStages: RemoteStageChoice[];
  shapeMismatch: boolean;
  shapeDiff: ShapeDiff | null;
  path: LinkPath | string;
}

type View = "connections" | "resume" | "tabs" | "stage-map";

const NOTICE_STYLES: Record<
  "warning" | "info" | "error",
  { wrap: string; icon: string; name: IconName }
> = {
  warning: {
    wrap: "border-[#FFAF53]/25 bg-[#FFAF53]/[0.06]",
    icon: "text-[#FFAF53]",
    name: "IconExclamationTriangle",
  },
  info: {
    wrap: "border-[#4C9AFF]/25 bg-[#4C9AFF]/[0.06]",
    icon: "text-[#4C9AFF]",
    name: "IconCircleInfo",
  },
  error: {
    wrap: "border-destructive/30 bg-destructive/[0.06]",
    icon: "text-destructive",
    name: "IconExclamationCircle",
  },
};

/**
 * Pull the claimant job off a REMOTE_JOB_ALREADY_LINKED 409 detail so the
 * notice can link straight to the Arbi job that now owns the remote job.
 */
function linkedJobFromError(
  err: ApiError,
): { id: string; title: string | null } | null {
  const id = err.detail.linked_job_id;
  if (typeof id !== "string" || !id) return null;
  const title = err.detail.linked_job_title;
  return { id, title: typeof title === "string" ? title : null };
}

/** Compact tinted notice — replaces the bulky two-column Alert. */
function NoticeBanner({
  variant,
  title,
  children,
  link,
}: {
  variant: "warning" | "info" | "error";
  title?: string;
  children: React.ReactNode;
  /** Optional trailing link (opens in a new tab), e.g. "view the linked job". */
  link?: { href: string; label: string };
}) {
  const s = NOTICE_STYLES[variant];
  return (
    <div className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 ${s.wrap}`}>
      <Icon name={s.name} size={15} className={`mt-[3px] shrink-0 ${s.icon}`} />
      <p className="text-[13px] leading-relaxed">
        {title && <span className="font-semibold text-heading">{title}. </span>}
        <span className="text-contrast/70">{children}</span>
        {link && (
          <>
            {" "}
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-baseline gap-0.5 font-medium text-heading underline decoration-contrast/30 underline-offset-2 hover:decoration-contrast/70"
            >
              {link.label}
              <Icon
                name="IconArrowUpRight"
                size={12}
                className="shrink-0 translate-y-[1px]"
              />
            </a>
          </>
        )}
      </p>
    </div>
  );
}

/** Selectable list row (a remote job or a connection) with hover affordance. */
function PickRow({
  label,
  icon,
  disabled,
  badge,
  onClick,
}: {
  label: string;
  icon: IconName;
  disabled?: boolean;
  badge?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-border/60 bg-surface-elevated/40 px-3 py-2.5 text-left transition-all hover:border-border hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:border-border/60 disabled:hover:bg-surface-elevated/40"
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-contrast/5 text-contrast/50 transition-colors group-hover:text-contrast/70 group-enabled:group-hover:bg-contrast/8">
          <Icon name={icon} size={14} />
        </span>
        <span className="truncate text-sm font-medium text-heading">
          {label}
        </span>
      </span>
      {badge ? (
        <span className="shrink-0 rounded-full bg-contrast/[0.06] px-2 py-0.5 text-[11px] font-medium text-contrast/45">
          {badge}
        </span>
      ) : (
        <Icon
          name="IconChevronRight"
          size={15}
          className="shrink-0 text-contrast/25 transition-all group-hover:translate-x-0.5 group-hover:text-contrast/60"
        />
      )}
    </button>
  );
}

function RowSkeleton() {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border/50 bg-surface-elevated/30 px-3 py-2.5">
      <div className="size-7 shrink-0 animate-pulse rounded-lg bg-contrast/6" />
      <div className="h-3.5 w-40 animate-pulse rounded bg-contrast/6" />
    </div>
  );
}

function ResumeStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1.5 rounded-lg bg-contrast/[0.04] px-2.5 py-1.5">
      <span className="text-sm font-semibold text-heading">{value}</span>
      <span className="text-xs text-contrast/55">{label}</span>
    </div>
  );
}

function EmptyHint({
  icon,
  title,
  body,
}: {
  icon: IconName;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-dashed border-border/70 bg-surface-elevated/30 px-4 py-8 text-center">
      <span className="mb-0.5 flex size-9 items-center justify-center rounded-full bg-contrast/5 text-contrast/40">
        <Icon name={icon} size={16} />
      </span>
      <p className="text-sm font-medium text-heading">{title}</p>
      <p className="max-w-[280px] text-[13px] leading-relaxed text-contrast/55">
        {body}
      </p>
    </div>
  );
}

export function JobLinkDialog({
  jobId,
  open,
  onOpenChange,
  onLinked,
  dormant,
  jobStages = [],
}: Props) {
  const { connections } = useAtsConnections();
  const active = connections.filter((c) => c.status === "active");
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("link");
  const [view, setView] = useState<View>("connections");
  const [resumeNote, setResumeNote] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  // When a remote job is already claimed by another Arbi job, the 409 detail
  // names it — surfaced as a "view the linked job" link on the notice.
  const [linkedJob, setLinkedJob] = useState<{
    id: string;
    title: string | null;
  } | null>(null);
  const [stageMapPending, setStageMapPending] =
    useState<StageMapPending | null>(null);
  const [stageMapValue, setStageMapValue] = useState<Record<string, string>>(
    {},
  );
  const [shapeAcknowledged, setShapeAcknowledged] = useState(false);
  // Spans the pre-link stage-map preview *and* the live link mutation so a
  // double-click on a remote-job row can't fire two overlapping link flows
  // (the second lands as a spurious 409 on an already-closed dialog).
  const [linking, setLinking] = useState(false);

  const link = useLinkJob(jobId);
  const { showJobIdsInPicker } = useConnectionSyncSettings(connectionId);
  const {
    jobs,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    syncPending,
    syncPendingTimedOut,
  } = useAtsRemoteJobs(connectionId, search);
  const jobsViewportRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    rootRef: jobsViewportRef,
    hasNextPage: !!hasNextPage,
    isFetching: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    enabled: view === "tabs",
  });

  // The dormant record carries the exact connection it was linked to, so
  // resume targets that connection directly instead of guessing by
  // connector name (two active connections can share the same connector).
  const resumeConnectionId = dormant?.connectionId ?? null;

  // Only (re-)initialize the dialog's starting view on the closed->open
  // transition. `dormant` is an object literal the parent recreates every
  // render, and refetchOnWindowFocus (global QueryClient default) or any
  // unrelated parent re-render while the dialog stays open must NOT re-fire
  // this and snap the user back to the Resume panel, discarding progress
  // they've made (e.g. switching to the picker, or being mid-stage-map).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      if (dormant && resumeConnectionId) {
        setConnectionId(resumeConnectionId);
        setView("resume");
      } else {
        setConnectionId(null);
        setView("connections");
      }
    }
    wasOpen.current = open;
  }, [open, dormant, resumeConnectionId]);

  const resumePreview = useRelinkPreview(
    jobId,
    resumeConnectionId,
    dormant?.remoteJobId ?? null,
    view === "resume",
  );

  useEffect(() => {
    if (view !== "resume" || !resumePreview.isError) return;
    const err = resumePreview.error;
    setLinkedJob(null);
    if (err instanceof ApiError && err.code === "REMOTE_JOB_ALREADY_LINKED") {
      setResumeNote(
        "That job is already linked to another Arbi job — pick a different one.",
      );
      setLinkedJob(linkedJobFromError(err));
    } else if (err instanceof ApiError && err.code === "REMOTE_JOB_NOT_FOUND") {
      setResumeNote(
        "That job could no longer be found in the ATS — pick another to relink.",
      );
    } else {
      setResumeNote("Couldn't preview the resume — pick a job to link.");
    }
    setView("tabs");
  }, [view, resumePreview.isError, resumePreview.error]);

  function reset() {
    setConnectionId(null);
    setSearch("");
    setTab("link");
    setView("connections");
    setResumeNote(null);
    setLinkError(null);
    setLinkedJob(null);
    setStageMapPending(null);
    setStageMapValue({});
    setShapeAcknowledged(false);
    setLinking(false);
  }

  function occupiedStagesFor(ids: string[]) {
    return ids.map((id) => ({
      id,
      name: jobStages.find((s) => s.id === id)?.name ?? id,
    }));
  }

  function enterStageMap(pending: StageMapPending) {
    setStageMapPending(pending);
    setShapeAcknowledged(false);
    setStageMapValue(
      suggestStageMap(
        occupiedStagesFor(pending.occupiedStageIds),
        pending.remoteStages,
      ),
    );
    setView("stage-map");
  }

  async function submitLink(payload: {
    connectionId: string;
    remoteJobId: string;
    candidateStageMap?: Record<string, string>;
    stageShapeAcknowledged?: boolean;
  }) {
    setLinkError(null);
    setLinkedJob(null);
    try {
      const response = await link.mutateAsync({
        data: {
          connection_id: payload.connectionId,
          remote_job_id: payload.remoteJobId,
          ...(payload.candidateStageMap
            ? { candidate_stage_map: payload.candidateStageMap }
            : {}),
          ...(payload.stageShapeAcknowledged
            ? { stage_shape_acknowledged: true }
            : {}),
        },
      });
      if (response.resumed) {
        const counts = response.counts ?? {};
        showToast.success(
          `Re-linked: pulled ${counts.to_pull ?? 0}, pushed ${
            counts.to_push ?? 0
          } stage updates, ${
            counts.conflicts_remote_wins ?? 0
          } conflicts resolved from ATS`,
        );
      }
      onLinked?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      if (err instanceof ApiError && err.code === "STAGE_MAP_REQUIRED") {
        const occupiedStageIds = Array.isArray(err.detail.occupied_stage_ids)
          ? (err.detail.occupied_stage_ids as string[])
          : [];
        // The 409 detail primarily carries occupied_stage_ids — remote stage
        // choices come from the already-fetched remote-jobs listing when the
        // detail omits them. Shape fields may be present on newer backends.
        const remoteStages: RemoteStageChoice[] = Array.isArray(
          err.detail.remote_stages,
        )
          ? (err.detail.remote_stages as RemoteStageChoice[])
          : (jobs.find((j) => j.remote_job_id === payload.remoteJobId)
              ?.stages ?? []);
        enterStageMap({
          connectionId: payload.connectionId,
          remoteJobId: payload.remoteJobId,
          occupiedStageIds,
          remoteStages,
          shapeMismatch: err.detail.shape_mismatch === true,
          shapeDiff: parseShapeDiff(err.detail.shape_diff),
          path:
            typeof err.detail.path === "string"
              ? err.detail.path
              : "first_link",
        });
        return;
      }
      if (err instanceof ApiError && err.code === "STAGE_SHAPE_ACK_REQUIRED") {
        const occupiedStageIds = Array.isArray(err.detail.occupied_stage_ids)
          ? (err.detail.occupied_stage_ids as string[])
          : [];
        const remoteStages: RemoteStageChoice[] = Array.isArray(
          err.detail.remote_stages,
        )
          ? (err.detail.remote_stages as RemoteStageChoice[])
          : (jobs.find((j) => j.remote_job_id === payload.remoteJobId)
              ?.stages ?? []);
        enterStageMap({
          connectionId: payload.connectionId,
          remoteJobId: payload.remoteJobId,
          occupiedStageIds,
          remoteStages,
          shapeMismatch: true,
          shapeDiff: parseShapeDiff(err.detail.shape_diff),
          path:
            typeof err.detail.path === "string"
              ? err.detail.path
              : "different_remote",
        });
        return;
      }
      if (err instanceof ApiError && err.code === "REMOTE_JOB_ALREADY_LINKED") {
        setLinkError(
          "That job is already linked to another Arbi job — pick a different one.",
        );
        setLinkedJob(linkedJobFromError(err));
        setView("tabs");
        return;
      }
      if (err instanceof ApiError && err.code === "REMOTE_JOB_NOT_FOUND") {
        setLinkError("That job could no longer be found in the ATS.");
        setView("tabs");
        return;
      }
      if (err instanceof ApiError && err.code === "REMOTE_JOB_NOT_OPEN") {
        setLinkError("This ATS job is not open");
        setView("tabs");
        return;
      }
      const message = err instanceof Error ? err.message : "Please try again.";
      showToast.error("Couldn't link job", { description: message });
    }
  }

  async function beginLink(remoteJobId: string) {
    if (!connectionId || linking) return;
    setLinking(true);
    try {
      // Stage comparison upfront — map and/or shape-ack before the live link
      // mutates anything (spec §6).
      try {
        const preview = await previewAtsLinkStageMap(jobId, {
          connection_id: connectionId,
          remote_job_id: remoteJobId,
        });
        if (preview.required) {
          enterStageMap({
            connectionId,
            remoteJobId,
            occupiedStageIds: preview.occupied_stage_ids ?? [],
            remoteStages: preview.remote_stages ?? [],
            shapeMismatch: !!preview.shape_mismatch,
            shapeDiff: parseShapeDiff(preview.shape_diff),
            path: preview.path ?? "first_link",
          });
          return;
        }
      } catch {
        // Preview failed — fall through to the live attempt (legacy 409 path).
      }
      await submitLink({ connectionId, remoteJobId });
    } finally {
      setLinking(false);
    }
  }

  async function handleLink(remoteJobId: string) {
    await beginLink(remoteJobId);
  }

  async function handleResume() {
    if (!resumeConnectionId || !dormant) return;
    // Same remote job — dry-run preview already surfaced reconcile counts.
    // Run the upfront stage-map check in case occupied stages need mapping
    // after a structure drift while dormant.
    await beginLink(dormant.remoteJobId);
  }

  async function handleStageMapSubmit() {
    if (!stageMapPending) return;
    const requireAck = requiresShapeAck(
      stageMapPending.shapeMismatch,
      stageMapPending.path,
    );
    await submitLink({
      connectionId: stageMapPending.connectionId,
      remoteJobId: stageMapPending.remoteJobId,
      candidateStageMap:
        Object.keys(stageMapValue).length > 0 ? stageMapValue : undefined,
      stageShapeAcknowledged: requireAck || undefined,
    });
  }

  const occupiedStages = useMemo(
    () => (stageMapPending ? occupiedStagesFor(stageMapPending.occupiedStageIds) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stageMapPending, jobStages],
  );

  const stageMapComplete =
    stageMapPending !== null &&
    stageMapPending.occupiedStageIds.every((id) => !!stageMapValue[id]);

  const requireAck =
    stageMapPending !== null &&
    requiresShapeAck(stageMapPending.shapeMismatch, stageMapPending.path);

  const canSubmitStageMap =
    stageMapComplete && (!requireAck || shapeAcknowledged) && !link.isPending;

  const linkedJobLink = linkedJob
    ? {
        href: `/jobs/${linkedJob.id}`,
        label: linkedJob.title
          ? `View “${linkedJob.title}”`
          : "View the linked job",
      }
    : undefined;

  const shapeOnly =
    stageMapPending !== null &&
    stageMapPending.occupiedStageIds.length === 0 &&
    stageMapPending.shapeMismatch;

  const title =
    view === "resume"
      ? "Resume ATS sync"
      : view === "stage-map"
        ? shapeOnly
          ? "Review pipeline changes"
          : "Map occupied stages"
        : connectionId
          ? "Pick or import a job"
          : "Connect this job to an ATS";

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <ModalContent>
        <ModalHeader title={title} showCloseButton={false} />
        <ModalBody>
          {view === "resume" && dormant && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#34D399]/10 text-[#34D399]">
                  <Icon name="IconArrowsRepeatRightLeft" size={17} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-heading">
                    Resume syncing with {dormant.connectorName}
                  </p>
                  <p className="text-[13px] text-contrast/55">
                    Pick up where this job left off.
                  </p>
                </div>
              </div>
              {resumePreview.isLoading && (
                <p className="text-[13px] text-contrast/50">
                  Checking what would sync…
                </p>
              )}
              {resumePreview.counts && (
                <div className="flex flex-wrap gap-2">
                  <ResumeStat label="to pull" value={resumePreview.counts.toPull} />
                  <ResumeStat label="to push" value={resumePreview.counts.toPush} />
                  <ResumeStat
                    label="conflicts from ATS"
                    value={resumePreview.counts.conflictsRemoteWins}
                  />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setView("tabs")}
                >
                  Link a different job
                </Button>
                <SubmitButton
                  type="button"
                  size="sm"
                  onClick={handleResume}
                  disabled={!resumePreview.counts || link.isPending || linking}
                  loading={link.isPending || linking}
                  loadingLabel="Resuming"
                >
                  Resume sync
                </SubmitButton>
              </div>
            </div>
          )}

          {view === "stage-map" && stageMapPending && (
            <div className="flex flex-col gap-4">
              <StageMapStep
                occupiedStages={occupiedStages}
                remoteStages={stageMapPending.remoteStages}
                value={stageMapValue}
                onChange={setStageMapValue}
                complete={stageMapComplete}
                shapeMismatch={stageMapPending.shapeMismatch}
                shapeDiff={stageMapPending.shapeDiff}
                shapeAcknowledged={shapeAcknowledged}
                onShapeAcknowledgedChange={setShapeAcknowledged}
                requireShapeAck={requireAck}
              />
              <div className="flex justify-end">
                <SubmitButton
                  type="button"
                  size="sm"
                  onClick={handleStageMapSubmit}
                  disabled={!canSubmitStageMap}
                  loading={link.isPending}
                  loadingLabel="Linking"
                >
                  Link job
                </SubmitButton>
              </div>
            </div>
          )}

          {view === "connections" && (
            <div className="flex flex-col gap-2">
              {active.length === 0 ? (
                <EmptyHint
                  icon="IconChainLink1"
                  title="No ATS connected"
                  body="Connect an ATS in Settings → Integrations to link this job."
                />
              ) : (
                <>
                  <p className="mb-0.5 px-0.5 text-xs font-medium uppercase tracking-wide text-contrast/40">
                    Pick a connection
                  </p>
                  {active.map((c) => (
                    <PickRow
                      key={c.id}
                      label={c.connector_name}
                      icon="IconChainLink1"
                      onClick={() => {
                        setConnectionId(c.id);
                        setView("tabs");
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          )}

          {view === "tabs" && (
            <div className="flex flex-col gap-3">
              {dormant && (
                <NoticeBanner
                  variant="warning"
                  title="Linking a different job resets sync"
                >
                  Candidates already synced from {dormant.connectorName} reset,
                  and re-syncing them costs credits.
                </NoticeBanner>
              )}
              {resumeNote && (
                <NoticeBanner variant="info" link={linkedJobLink}>
                  {resumeNote}
                </NoticeBanner>
              )}
              {linkError && (
                <NoticeBanner variant="error" link={linkedJobLink}>
                  {linkError}
                </NoticeBanner>
              )}
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                  <TabsTrigger value="link">Link existing</TabsTrigger>
                  <TabsTrigger
                    value="import"
                    disabled
                    title="Import requires project setup — coming soon"
                  >
                    Import new
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="link" className="mt-3 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="relative min-w-0 flex-1">
                      <Icon
                        name="IconMagnifyingGlass"
                        size={15}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-contrast/40"
                      />
                      <Input
                        className="pl-9"
                        placeholder="Search ATS jobs…"
                        value={search}
                        disabled={syncPending}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    {syncPending && (
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-contrast/55">
                        <span className="ai-ping-dot" />
                        {syncPendingTimedOut
                          ? "Still syncing, try again later"
                          : "Syncing"}
                      </span>
                    )}
                  </div>
                  <div
                    ref={jobsViewportRef}
                    className="-mr-1 flex max-h-[320px] flex-col gap-1.5 overflow-y-auto pr-1"
                  >
                    {isLoading ? (
                      <>
                        <RowSkeleton />
                        <RowSkeleton />
                        <RowSkeleton />
                        <RowSkeleton />
                      </>
                    ) : jobs.length === 0 && !syncPending ? (
                      <EmptyHint
                        icon="IconMagnifyingGlass"
                        title={search ? "No matches" : "No jobs found"}
                        body={
                          search
                            ? `Nothing matches “${search}”.`
                            : "This connection has no jobs available to link yet."
                        }
                      />
                    ) : (
                      <>
                        {jobs.map((j) => {
                          const selectable = isRemoteJobSelectable(
                            j.status,
                            j.already_linked,
                          );
                          const closedBadge = remoteJobClosedBadge(j.status);
                          return (
                            <PickRow
                              key={j.remote_job_id}
                              label={formatAtsJobLabel(
                                j.title,
                                j.remote_job_id,
                                showJobIdsInPicker,
                              )}
                              icon="IconSuitcaseWork"
                              disabled={!selectable || link.isPending || linking}
                              badge={
                                j.already_linked
                                  ? "Already linked"
                                  : closedBadge
                              }
                              onClick={() => handleLink(j.remote_job_id)}
                            />
                          );
                        })}
                        {isFetchingNextPage && (
                          <>
                            <RowSkeleton />
                            <RowSkeleton />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="import" className="mt-3">
                  <EmptyHint
                    icon="IconSparkle"
                    title="Import coming soon"
                    body="Importing a new job from the ATS needs project setup. For now, link to an existing Arbi job."
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
